import React, { Fragment, HTMLAttributes } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { WizardStepMenu, StepProps, Header, ParagraphText } from '..';

const { Overlay } = Dialog;

export interface QuickSetupModalProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'sm' | 'default' | 'lg' | 'xl';
  isOpen: boolean;
  setIsOpen: (x?: any) => any | void;
  header?: string;
  subHeader?: string;
  steps?: StepProps[];
  inProgress?: number;
  displayStepNumbers?: boolean;
  hideLeftSide?: boolean;
  width?: string;
  disableBackdropClick?: boolean;
}

export const QuickSetupModal = ({
  children,
  isOpen,
  variant = 'xl',
  setIsOpen,
  header,
  subHeader,
  steps,
  inProgress,
  displayStepNumbers,
  hideLeftSide,
  width,
  disableBackdropClick = false,
  ...rest
}: QuickSetupModalProps) => {
  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          open={isOpen}
          onClose={() => (disableBackdropClick ? () => {} : setIsOpen(false))}
          className={`fixed z-100 inset-0 w-screen h-screen overflow-y-auto`}
        >
          <div className="min-h-screen px-4 text-center z-100" {...rest}>
            <Transition.Child
              as="div"
              enter="ease-in-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Overlay className="fixed inset-0 bg-thryv-black-500 opacity-60 p-[30px]" />
            </Transition.Child>
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {/* This element is to trick the browser into centering the modal contents. */}
              <span
                className="inline-block h-screen align-middle sm:align-top md:align-top lg:align-middle xl:align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>

              <div
                className={`inline-block bg-white box-content
              ${
                variant === 'sm'
                  ? 'w-300px lg:w-2/4 md:w-2/4 sm:w-2/4 h-auto py-2 px-[16px]'
                  : variant === 'default'
                  ? 'w-[500px] lg:w-3/4 md:w-3/4 h-auto py-[22px] px-[32px]'
                  : variant === 'lg'
                  ? 'w-3/4 lg:w-3/4 md:w-3/4 h-auto py-[22px] px-[32px]'
                  : 'w-3/4 lg:w-3/4 md:w-3/4 h-auto py-[22px] px-[32px]'
              }
              p-2 my-4 overflow-hidden text-left align-middle transition-all transform shadow-thryv rounded border border-thryv-gray-light-400`}
                style={width ? { width: width } : undefined}
                /* DO NOT DELETE id="modal". IT IS USED TO ADD EVENT LISTENER CORRECTLY WHEN THE DATEPICKER IS IN A MODAL */
                id="modal"
              >
                <div
                  className={`grid ${
                    hideLeftSide ? `grid-cols-[100%]` : `grid-cols-[30%70%]`
                  } gap-4 sm:flex `}
                >
                  {/* Left Side Content */}
                  <div
                    className={`flex flex-col gap-[15px] border-r border-[#ECEEF1] p-3 ${
                      hideLeftSide && `hidden`
                    } sm:hidden`}
                  >
                    {header ? (
                      <Header variant="h2" fontWeight="semibold">
                        {header}
                      </Header>
                    ) : null}

                    {subHeader ? (
                      <ParagraphText variant="reg" color="thryv-steel">
                        {subHeader}
                      </ParagraphText>
                    ) : null}
                    {steps && inProgress !== undefined ? (
                      <WizardStepMenu
                        direction={'vertical'}
                        steps={steps}
                        inProgress={inProgress}
                        size="small"
                        displayStepNumbers={displayStepNumbers}
                      />
                    ) : null}
                  </div>
                  {/* Right Side Content */}
                  <div className={`p-3`}>{children}</div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
